/**
 * Serviço de Turmas
 *
 * Gerencia operações CRUD de turmas usando PostgREST.
 * Operações complexas (estatísticas) usam RPC functions.
 */

import { postgrestClient } from "../lib/postgrest/client";

/**
 * Converte uma data para o dia da semana em português (SEGUNDA, TERCA, etc.)
 * @param {string|Date} data - Data no formato YYYY-MM-DD ou objeto Date
 * @returns {string} Dia da semana em maiúsculas
 */
const converterDataParaDiaSemana = (data) => {
  const diasSemana = ['DOMINGO', 'SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
  let dateObj;

  if (data instanceof Date) {
    dateObj = data;
  } else if (typeof data === 'string') {
    dateObj = new Date(data + 'T00:00:00'); // Adiciona hora para evitar problemas de timezone
  } else {
    dateObj = new Date();
  }

  const diaSemana = dateObj.getDay(); // 0 = Domingo, 1 = Segunda, etc.
  return diasSemana[diaSemana];
};

/**
 * Serviço de turmas
 */
export const turmasService = {
  /**
   * Lista turmas com filtros
   * @param {Object} options
   * @param {Object} options.filters - Filtros (ex: { nucleo_id: "eq.123", sala_id: "eq.456" })
   * @param {string} options.order - Ordenação (ex: "nome.asc")
   * @param {number} options.limit - Limite de resultados
   * @param {number} options.offset - Offset para paginação
   * @param {boolean} options.includeCount - Se true, retorna { data, total }
   * @returns {Promise<Array|{data: Array, total: number}>}
   */
  async list(options = {}) {
    const { filters = {}, order = "horario.asc", limit, offset, includeCount = false } = options;

    // Usar view otimizada que inclui contagem de alunos
    const result = await postgrestClient.get("vw_turmas_metricas", {
      filters,
      order: order.includes("nome") ? undefined : order, // Não ordenar por nome no PostgREST se especificado
      limit,
      offset,
      includeCount,
    });

    // Extrai data e total se includeCount for true
    let data = result;
    let total = null;
    if (includeCount && result && typeof result === 'object' && 'data' in result) {
      data = result.data;
      total = result.total;
    }

    // Ordenar no frontend se order foi especificado e incluir nome
    // Como nome pode ser NULL, ordenar no frontend
    if (order && order.includes("nome")) {
      const dataArray = Array.isArray(data) ? data : data ? [data] : [];
      const sorted = dataArray.sort((a, b) => {
        const nomeA = (a.nome || "").toLowerCase();
        const nomeB = (b.nome || "").toLowerCase();
        return order.includes("desc") ? nomeB.localeCompare(nomeA) : nomeA.localeCompare(nomeB);
      });
      data = sorted;
    }

    // Retorna { data, total } se includeCount, senão retorna array
    if (includeCount) {
      return { data: Array.isArray(data) ? data : data ? [data] : [], total: total !== null ? total : (Array.isArray(data) ? data.length : 1) };
    }

    return data;
  },

  /**
   * Busca turma por ID
   * @param {string} id
   * @returns {Promise<Object>}
   */
  async getById(id) {
    const result = await postgrestClient.get("turmas", {
      filters: { turma_id: `eq.${id}` },
    });
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Cria nova turma
   * @param {Object} turmaData
   * @returns {Promise<Object>}
   */
  async create(turmaData) {
    // Views geralmente não são atualizáveis, usar tabela turma diretamente
    const result = await postgrestClient.post("turma", turmaData);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Atualiza turma
   * @param {string} id
   * @param {Object} turmaData
   * @returns {Promise<Object>}
   */
  async update(id, turmaData) {
    // Views geralmente não são atualizáveis, usar tabela turma diretamente
    const result = await postgrestClient.patch("turma", { turma_id: id }, turmaData);
    return Array.isArray(result) ? result[0] : result;
  },

  /**
   * Remove turma
   * @param {string} id
   * @returns {Promise<null>}
   */
  async delete(id) {
    // Views geralmente não são atualizáveis, usar tabela turma diretamente

    try {
      const result = await postgrestClient.delete("turma", { turma_id: id });

      return result;
    } catch (error) {
      console.error("[turmasService.delete] Erro ao excluir turma:", {
        id,
        error,
        message: error?.message,
        status: error?.status,
        data: error?.data
      });
      throw error;
    }
  },

  /**
   * Busca turmas do dia
   * @param {string|Date} data - Data no formato YYYY-MM-DD ou objeto Date
   * @returns {Promise<Array>}
   */
  async getByDate(data) {
    // Converte data para dia da semana
    const diaSemana = converterDataParaDiaSemana(data);

    // Busca todas as turmas ativas primeiro
    const todasTurmas = await postgrestClient.get("turmas", {
      filters: { ativa: `eq.true` },
    });

    if (!Array.isArray(todasTurmas)) {
      return [];
    }

    // Filtra turmas que têm o dia da semana no array dias_semana ou no horarios_config
    const turmasDoDia = todasTurmas.filter(turma => {
      // Verifica no array dias_semana (formato antigo)
      if (turma.dias_semana && Array.isArray(turma.dias_semana)) {
        const diasNormalizados = turma.dias_semana.map(d =>
          String(d).trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        );
        if (diasNormalizados.includes(diaSemana)) {
          return true;
        }
      }

      // Verifica no horarios_config (formato novo)
      if (turma.horarios_config && typeof turma.horarios_config === 'object') {
        // horarios_config pode ser um objeto com dias como chaves ou um array
        if (Array.isArray(turma.horarios_config)) {
          const temDia = turma.horarios_config.some(config => {
            const diaConfig = config.dia || config.diaSemana;
            if (diaConfig) {
              const diaNormalizado = String(diaConfig).trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
              return diaNormalizado === diaSemana;
            }
            return false;
          });
          if (temDia) return true;
        } else if (typeof turma.horarios_config === 'object') {
          // Se for objeto, verifica se tem a chave do dia
          const chavesDias = Object.keys(turma.horarios_config);
          const temDia = chavesDias.some(chave => {
            const diaNormalizado = String(chave).trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            return diaNormalizado === diaSemana || diaNormalizado.includes(diaSemana);
          });
          if (temDia) return true;
        }
      }

      return false;
    });

    return turmasDoDia;
  },

  /**
   * Busca estatísticas de uma turma (RPC function)
   * @param {string} turmaId
   * @returns {Promise<Object>}
   */
  async getEstatisticas(turmaId) {
    return postgrestClient.rpc("turma_estatisticas", {
      p_turma_id: turmaId,
    });
  },

  /**
   * Busca alunos vinculados a uma turma
   * @param {string} turmaId
   * @returns {Promise<Array>}
   */
  async getAlunosVinculados(turmaId) {
    // Usa view ou RPC function se disponível
    return postgrestClient.get("alunos_por_turma", {
      filters: { turma_id: `eq.${turmaId}` },
    });
  },

  /**
   * Busca monitores vinculados aos horários de uma turma
   * Usa view otimizada para evitar N+1 (monitor_id -> nome)
   * @param {string} turmaId
   * @returns {Promise<Array>}
   */
  async getMonitoresHorarios(turmaId) {
    return postgrestClient.get("vw_monitores_horarios", {
      filters: { turma_id: `eq.${turmaId}` },
      order: "dia_semana.asc,ordem.asc",
    });
  },
};

export default turmasService;

